import React, { useEffect, useRef } from 'react';

/**
 * Ambient "liquid sand" backdrop.
 *
 * A fixed, full-viewport canvas that paints a grainy sand veil over the page's
 * hero engraving (the `.App` background). On click/tap — and while dragging —
 * the sand "parts" in a soft ripple, uncovering the bright copper engraving
 * beneath, then flows closed again as the reveal heals.
 *
 * It is purely decorative: `pointer-events: none` plus window-level listeners
 * mean it never intercepts clicks meant for the real UI. Honors
 * prefers-reduced-motion by not mounting at all (the bright bg shows statically).
 */

const REST_ALPHA = 0.72;     // sand coverage at rest (engraving stays muted)
const HEAL_PER_MS = 0.0016;  // openness decay/ms (~600ms for a tap to settle)
const BASE_RADIUS = 120;     // px reveal radius
const GROW = 70;             // px the ripple expands across its life
const GROW_MS = 900;         // ms to reach full expansion
const MAX_REVEALS = 60;
const MOVE_THROTTLE_MS = 28;

interface Reveal {
  x: number;
  y: number;
  openness: number;
  born: number;
  r0: number;
}

const LiquidSandBackdrop: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const reveals: Reveal[] = [];
    let raf = 0;
    let lastT = 0;
    let settled = false;
    let dragging = false;
    let lastMove = 0;

    const clamp = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v);

    // Build the warm, grainy sand tile once, then repeat it as a fill pattern.
    const tileSize = 140;
    const tile = document.createElement('canvas');
    tile.width = tile.height = tileSize;
    const tctx = tile.getContext('2d')!;
    tctx.fillStyle = '#2a2622';
    tctx.fillRect(0, 0, tileSize, tileSize);
    const imgData = tctx.getImageData(0, 0, tileSize, tileSize);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
      const n = (Math.random() * 2 - 1) * 22; // grain, warm-biased
      d[i] = clamp(d[i] + n);
      d[i + 1] = clamp(d[i + 1] + n * 0.9);
      d[i + 2] = clamp(d[i + 2] + n * 0.8);
    }
    tctx.putImageData(imgData, 0, 0);
    const pattern = ctx.createPattern(tile, 'repeat');

    const paintRest = (w: number, h: number) => {
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = REST_ALPHA;
      ctx.fillStyle = pattern || '#2a2622';
      ctx.fillRect(0, 0, w, h);
      ctx.globalAlpha = 1;
    };

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      settled = false; // force a repaint at the new size
    };
    resize();

    const addReveal = (x: number, y: number, strength: number) => {
      if (reveals.length >= MAX_REVEALS) reveals.shift();
      reveals.push({
        x,
        y,
        openness: strength,
        born: performance.now(),
        r0: BASE_RADIUS * (0.7 + Math.random() * 0.4),
      });
      if (settled) {
        settled = false;
        raf = requestAnimationFrame(frame);
      }
    };

    const frame = (t: number) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const dt = lastT ? t - lastT : 16;
      lastT = t;

      if (reveals.length === 0 && settled) {
        raf = requestAnimationFrame(frame); // idle spin, canvas already clean
        return;
      }

      paintRest(w, h);

      // Carve the reveals out of the veil.
      ctx.globalCompositeOperation = 'destination-out';
      for (const rv of reveals) {
        const r = rv.r0 + GROW * Math.min(1, (t - rv.born) / GROW_MS);
        const s = Math.max(0, rv.openness);
        const g = ctx.createRadialGradient(rv.x, rv.y, 0, rv.x, rv.y, r);
        g.addColorStop(0, `rgba(0,0,0,${0.95 * s})`);
        g.addColorStop(0.55, `rgba(0,0,0,${0.72 * s})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(rv.x, rv.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // A faint copper bloom where the sand has parted.
      ctx.globalCompositeOperation = 'lighter';
      for (const rv of reveals) {
        const r = (rv.r0 + GROW * Math.min(1, (t - rv.born) / GROW_MS)) * 0.9;
        const s = Math.max(0, rv.openness);
        const g = ctx.createRadialGradient(rv.x, rv.y, 0, rv.x, rv.y, r);
        g.addColorStop(0, `rgba(240,138,36,${0.1 * s})`);
        g.addColorStop(1, 'rgba(240,138,36,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(rv.x, rv.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';

      // Heal, then drop fully-settled reveals.
      for (const rv of reveals) rv.openness -= HEAL_PER_MS * dt;
      for (let i = reveals.length - 1; i >= 0; i--) {
        if (reveals[i].openness <= 0.02) reveals.splice(i, 1);
      }

      if (reveals.length === 0) {
        paintRest(w, h); // clean frame so no faint hole lingers
        settled = true;
      }
      raf = requestAnimationFrame(frame);
    };

    const onDown = (e: PointerEvent) => {
      dragging = true;
      addReveal(e.clientX, e.clientY, 1);
    };
    const onUp = () => {
      dragging = false;
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const now = performance.now();
      if (now - lastMove < MOVE_THROTTLE_MS) return;
      lastMove = now;
      addReveal(e.clientX, e.clientY, 0.8);
    };
    const onVisibility = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) {
        lastT = 0;
        raf = requestAnimationFrame(frame);
      }
    };

    window.addEventListener('resize', resize);
    window.addEventListener('pointerdown', onDown, { passive: true });
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('pointercancel', onUp, { passive: true });
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} className="liquid-sand-backdrop" aria-hidden="true" />;
};

export default LiquidSandBackdrop;
